// PenParamsSection — ペン別パラメータの共有セクション (Modern / Classic 両用).
//
// 背景 (modern-brush-panel-2026-08-17.md §8 / §9-1): これまで `PenKindPicker` が
// (a) ペン種チップ列 + (b) ペン別パラメータ (texture preset サムネ / airbrush の
// grain・density スライダ) の合成だったのを、共有単位を (b) 側に 1 段下げるために
// 切り出した。表現パリティ原則 (Classic/Modern で同じ挙動が出ること) を安く担保して
// いたのは実際には (a) の見た目ではなく (b) が両レイアウトへ相乗りしていたことなので、
// 共有部品としての本体はこちらに移す。
//
// **今後、新しいペン種にペン別パラメータ UI (texture の preset / airbrush の
// grain・density のような「そのペンを選んだときだけ出す調整 UI」) を足すときは、
// 追加先はここ 1 箇所でよい** — Modern (ツールプロパティ) / Classic (フロート「ペン」)
// の両方に自動で反映される。
//
// - Modern: `ModernToolProperties` のパネル内、ペン種チップ (`PenKindPicker` 経由、
//   後続タスクでブラシパネルへ独立予定) の後段に置く。
// - Classic: 常設フロート「ペン」(`PenPopup`) の中身として `PenKindPicker` 経由で置く。
//
// 本部品は「(pen==='texture' のとき) texture preset サムネ」「(pen==='airbrush' のとき)
// grain/density スライダ」だけを担う純粋な部品 (= レイアウト非依存)。pen='texture' への
// 切替・penParams 反映は呼び出し側 (= 共有 chrome の `onSelectTexturePreset` /
// `onAirbrushGrainChange` 等、penParamsFor) の責務で、本部品は「どの preset / どの値を
// 選ぶか」だけを表現する (= engine / protocol 不変)。
//
// **抽出前の `PenKindPicker` インライン実装と DOM/挙動は不変** (= `PenKindPicker` から
// そのまま切り出しただけの純粋なリファクタリング、modern-brush-panel-2026-08-17.md §9-1)。
//
// ## 追記 (縦圧解消の第2弾、実機フィードバック 2026-08-17)
//
// テクスチャの4プリセットは常設「ブラシ」パネル (`ModernBrushPanel`) の item 行へ昇格した
// (= 1クリックで pen='texture' と texturePreset が同時に決まる)。**Modern の
// ツールプロパティ (`ModernToolProperties`) からは preset サムネの二重表示をやめる**が、
// **Classic (`PenPopup` → `PenKindPicker`) は現状維持** (= まだペン種チップを押した後に
// preset を選ぶ2階層 UI のまま、懐古 UI を変えない)。この分岐を安全に両立させるため、
// `showTexturePreset` (呼び出し側が明示、デフォルト無し) を追加した。
// - `PenKindPicker` (Classic 専用に確定): 常に `showTexturePreset={true}`。
// - `ModernToolProperties`: 常に `showTexturePreset={false}`。
// airbrush の grain/density スライダは対象外 (= 連続値は「描きながら調整」する対象なので
// 両レイアウトともここに残す、引き続き無条件表示)。
//
// ## クレヨン / パステルの目の粗さ (phase11-light-pens-2026-08-21.md §2.6 / §6)
//
// 3 段階の離散値 (0=細/1=中/2=粗) なので、連続スライダ (tone 番手・airbrush grain/density)
// ではなく **segmented ボタン** で出す (`ModernToolProperties.tsx` の手ぶれ補正
// off/弱/中/強と同じ `<button aria-pressed>` の慣行、useSemanticElements が
// `<input type=radio>` を要求する `role="radio"` は避ける)。crayon/pastel は互いに排他
// (pen が同時に両方になることはない) なので同じラベル文言・同じ helper を共用する。
//
// ## トーンの封印解除 + pitch パラメータ化 (phase11-light-pens-2026-08-21.md §4.5-§4.7)
//
// トーンの番手 (density) スライダは元々 `ModernToolProperties.tsx` にインラインで実装
// されていた (= Classic は tone を露出しない前提だったので独立コードパスで足りていた)。
// 封印解除にあたり **表現パリティ原則で Classic にも同じ UI を出す必要がある**ため、
// airbrush/crayon/pastel と同じ「共有 `PenParamsSection` に相乗り」方式へ揃えた
// (= 追加先はここ 1 箇所で Modern/Classic 両方に反映される、本ファイル冒頭コメント参照)。
// 新規の pitch (間隔、8/16/32/64 の 4 段階) は番手と同じ fieldset 内に並べる
// (§4.7「間隔スライダ + ドット径スライダの 2 本」)。`4` は柄として成立しないことが実機で
// 確認済みなので選択肢から外す (§4.3/§4.5)。
//
// ## ドットの露出 (同 §3 / §6)
//
// spacing (間隔) 1 本のみの連続スライダ。**単位は掃引ステップであって px ではない**
// (§3.4) ので、ラベルは「間隔」のみで px を名乗らない (airbrush 噴霧密度と同じ
// 「ラベル+値+スライダ」の形だが、値表示に単位サフィックスを付けない点が異なる)。

import type { StrokePen, TexturePreset } from '@oekaki/protocol';

import { TexturePresetPicker } from './TexturePresetPicker.js';

/** クレヨン/パステルの目の粗さ (3段階、drawing-core の grain index 0..2 とそのまま対応)。 */
const GRAIN_OPTIONS: ReadonlyArray<{ readonly value: 0 | 1 | 2; readonly label: string }> = [
  { value: 0, label: '細' },
  { value: 1, label: '中' },
  { value: 2, label: '粗' },
];

/**
 * トーンのドット間隔 (pitch, 4段階、phase11-light-pens-2026-08-21.md §4.5)。seamless
 * tiling 制約 (256 の約数) を満たす候補は `[4, 8, 16, 32, 64]` だが、**`4` は柄として
 * 成立しないことが実機で確認済みなので UI の選択肢から外す** (§4.3/§4.5、`8` は網点寄り
 * として残す)。既定は `16` (`DEFAULT_TONE_PITCH`、`PenConfig.ts`)。
 */
const TONE_PITCH_OPTIONS: ReadonlyArray<{
  readonly value: 8 | 16 | 32 | 64;
  readonly label: string;
}> = [
  { value: 8, label: '8' },
  { value: 16, label: '16' },
  { value: 32, label: '32' },
  { value: 64, label: '64' },
];

/** grain segmented ボタンの chip class (`ModernToolProperties.inlineChipClass` と同型)。 */
const grainChipClass = (active: boolean): string =>
  `min-w-0 flex-1 rounded border px-1 py-0.5 text-center text-[10px] font-bold transition-colors ${
    active
      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
      : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]'
  }`;

/**
 * クレヨン/パステル共通の「目の粗さ」segmented (3段階) を組み立てる。
 *
 * **あえて子コンポーネントにせず、JSX を直接返す関数として呼ぶ** (`GrainSegmented(...)`
 * を式の中で直接呼び出す、`<GrainSegmented />` のような要素化はしない): この
 * ファイルのテスト (`PenParamsSection.test.tsx`) は `PenParamsSection` の戻り値を
 * `isValidElement` で tree-walk し、**要素の `type` が文字列 (= DOM タグ) のときだけ
 * 子要素へ再帰する** 慣行 (= 関数コンポーネントは呼ばず opaque な leaf 扱い、node
 * 環境で安全に検証するための既存パターン、`TexturePresetPicker` 等)。`<GrainSegmented
 * key=.../>` と JSX 化すると要素の `type` が関数参照になり、内部の `<fieldset>` 以下が
 * tree-walk から見えなくなってテストが検証できなくなる。関数として直接呼べば戻り値の
 * ルートがそのまま `<fieldset>` (文字列 type) になり、呼び出し元の配列に直接フラットに
 * 埋め込める。
 */
const renderGrainSegmented = (
  key: string,
  fieldsetLabel: string,
  value: number,
  onChange: (grain: number) => void,
): React.ReactNode => (
  <fieldset key={key} className="m-0 flex flex-col gap-1 border-0 p-0" aria-label={fieldsetLabel}>
    <legend className="mb-1 select-none text-[10px] text-[var(--color-text-muted)]">
      目の粗さ
    </legend>
    <div className="flex gap-1">
      {GRAIN_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={value === opt.value}
          aria-label={`目の粗さ ${opt.label}`}
          onClick={() => onChange(opt.value)}
          className={grainChipClass(value === opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </fieldset>
);

export interface PenParamsSectionProps {
  /** 現在のペン種 (= どのペン別パラメータ UI を出すかの判定に使う)。 */
  readonly pen: StrokePen;
  /**
   * texture preset セクションを出すか (2026-08-17 追記)。Classic (`PenKindPicker`) は
   * 常に `true`、Modern (`ModernToolProperties`) は常に `false`
   * (= preset 選択は常設「ブラシ」パネルへ移設済み)。呼び出し側に明示させることで、
   * どちらか一方だけ変更漏れするデグレを防ぐ (default を持たせない)。
   */
  readonly showTexturePreset: boolean;
  /** 現在の texture preset (= `pen==='texture'` のとき preset サムネのハイライト対象)。 */
  readonly texturePreset: TexturePreset;
  /** texture preset サムネクリックで発火。呼び出し側が pen.texturePreset を書く。 */
  readonly onSelectTexturePreset: (preset: TexturePreset) => void;
  /**
   * エアブラシの粒の粗さ (grain, 0..3) — `pen==='airbrush'` のときだけスライダを出す
   * (airbrush-design-2026-08-15.md §1.3 / §6)。`pen.airbrushGrain` を渡す。
   */
  readonly airbrushGrain: number;
  /** grain スライダ変更で発火。呼び出し側が pen.airbrushGrain を書く。 */
  readonly onAirbrushGrainChange: (grain: number) => void;
  /**
   * エアブラシの噴霧密度 (density, 0..1) — `pen==='airbrush'` のときだけスライダを出す
   * (design §1.3 / §6)。`pen.airbrushDensity` を渡す。
   */
  readonly airbrushDensity: number;
  /** density スライダ変更で発火。呼び出し側が pen.airbrushDensity を書く。 */
  readonly onAirbrushDensityChange: (density: number) => void;
  /**
   * クレヨンの目の粗さ (grain, 0..2) — `pen==='crayon'` のときだけ segmented を出す
   * (phase11-light-pens-2026-08-21.md §2.6 / §6)。`pen.crayonGrain` を渡す。
   */
  readonly crayonGrain: number;
  /** grain 変更で発火。呼び出し側が pen.crayonGrain を書く。 */
  readonly onCrayonGrainChange: (grain: number) => void;
  /**
   * パステルの目の粗さ (grain, 0..2) — `pen==='pastel'` のときだけ segmented を出す
   * (同 §2.6 / §6)。`pen.pastelGrain` を渡す。
   */
  readonly pastelGrain: number;
  /** grain 変更で発火。呼び出し側が pen.pastelGrain を書く。 */
  readonly onPastelGrainChange: (grain: number) => void;
  /**
   * トーンの番手 (density, 0..1 連続) — `pen==='tone'` のときだけ番手スライダ + 間隔
   * segmented を出す (texture-brush-design.md §12.2 / phase11-light-pens-2026-08-21.md
   * §4.5-§4.7、封印解除)。`pen.toneDensity` を渡す。
   */
  readonly toneDensity: number;
  /** 番手スライダ変更で発火。呼び出し側が pen.toneDensity を書く。 */
  readonly onToneDensityChange: (density: number) => void;
  /**
   * トーンのドット間隔 (pitch, 8/16/32/64 の 4 段階) — `pen==='tone'` のときだけ出す
   * (同 §4.5-§4.7)。`pen.tonePitch` を渡す。
   */
  readonly tonePitch: number;
  /** 間隔 segmented 変更で発火。呼び出し側が pen.tonePitch を書く。 */
  readonly onTonePitchChange: (pitch: number) => void;
  /**
   * ドットの間隔 (spacing, 1..200 連続、掃引ステップ単位で px ではない) — `pen==='dot'`
   * のときだけ出す (同 §3.4/§3.5/§6)。`pen.dotSpacing` を渡す。
   */
  readonly dotSpacing: number;
  /** 間隔スライダ変更で発火。呼び出し側が pen.dotSpacing を書く。 */
  readonly onDotSpacingChange: (spacing: number) => void;
}

/**
 * ペン別パラメータ (texture preset サムネ / airbrush grain・density スライダ)。
 * `pen` に応じて該当セクションだけを出す (= 互いに排他)。余白等は最小に保ち、親
 * (`PenKindPicker` の `flex flex-col gap-3`) 側のレイアウトに委ねる。
 */
export const PenParamsSection = ({
  pen,
  showTexturePreset,
  texturePreset,
  onSelectTexturePreset,
  airbrushGrain,
  onAirbrushGrainChange,
  airbrushDensity,
  onAirbrushDensityChange,
  crayonGrain,
  onCrayonGrainChange,
  pastelGrain,
  onPastelGrainChange,
  toneDensity,
  onToneDensityChange,
  tonePitch,
  onTonePitchChange,
  dotSpacing,
  onDotSpacingChange,
}: PenParamsSectionProps): React.ReactNode => [
  // テクスチャ preset セクション (pen==='texture' かつ showTexturePreset のときだけ、
  // 2階層 UI の 2 段目)。preset サムネグリッドは Modern / Classic で共有
  // (`TexturePresetPicker`)。Modern は showTexturePreset=false を渡すので出ない
  // (= preset 選択は常設「ブラシ」パネルへ移設済み、2026-08-17 追記)。
  //
  // 配列 (Fragment でなく) で返している理由: `PenKindPicker` がこの部品を挿す位置は
  // 抽出前は直接インラインの JSX 兄弟要素だった。DOM 上は Fragment でも配列でも
  // ラッパー要素を作らないため見た目は同じだが、`pen` に応じて 0/1 要素を出し分ける
  // 素朴な構造をそのまま保つのに配列の方が扱いやすい (= texture/airbrush は互いに排他)。
  pen === 'texture' && showTexturePreset ? (
    <fieldset key="texture" className="m-0 border-0 p-0" aria-label="テクスチャ">
      <legend className="mb-1 select-none text-[10px] text-[var(--color-text-muted)]">
        テクスチャ
      </legend>
      <TexturePresetPicker value={texturePreset} onSelectTexturePreset={onSelectTexturePreset} />
    </fieldset>
  ) : null,

  // エアブラシ grain/density スライダ (pen==='airbrush' のときだけ、
  // airbrush-design-2026-08-15.md §6)。texture preset と同様この部品に相乗りすること
  // で、表現パリティ原則 (§0) どおり Classic (PenPopup) / Modern
  // (ModernToolProperties) の両方に自動で出る。
  pen === 'airbrush' ? (
    <fieldset
      key="airbrush"
      className="m-0 flex flex-col gap-2 border-0 p-0"
      aria-label="エアブラシ"
    >
      <legend className="mb-1 select-none text-[10px] text-[var(--color-text-muted)]">
        エアブラシ
      </legend>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="select-none text-[10px] text-[var(--color-text-muted)]">粒の粗さ</span>
          <span className="select-none font-mono text-[10px] text-white">{airbrushGrain}</span>
        </div>
        <input
          type="range"
          min={0}
          max={3}
          step={1}
          value={airbrushGrain}
          onChange={(e) => onAirbrushGrainChange(Number(e.currentTarget.value))}
          aria-label="粒の粗さ"
          className="w-full accent-[var(--color-accent)]"
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="select-none text-[10px] text-[var(--color-text-muted)]">噴霧密度</span>
          <span className="select-none font-mono text-[10px] text-white">
            {Math.round(airbrushDensity * 100)}%
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={airbrushDensity}
          onChange={(e) => onAirbrushDensityChange(Number(e.currentTarget.value))}
          aria-label="噴霧密度"
          className="w-full accent-[var(--color-accent)]"
        />
      </div>
    </fieldset>
  ) : null,

  // クレヨン/パステルの目の粗さ segmented (pen に応じて片方だけ、phase11-light-pens-
  // 2026-08-21.md §2.6 / §6)。texture preset・airbrush と同様この部品に相乗りすることで、
  // 表現パリティ原則どおり Classic (PenPopup) / Modern (ModernToolProperties) の両方に
  // 自動で出る。
  pen === 'crayon'
    ? renderGrainSegmented('crayon', 'クレヨン', crayonGrain, onCrayonGrainChange)
    : null,

  pen === 'pastel'
    ? renderGrainSegmented('pastel', 'パステル', pastelGrain, onPastelGrainChange)
    : null,

  // トーン (pen==='tone' のときだけ、texture-brush-design.md §12.2 / phase11-light-pens-
  // 2026-08-21.md §4.5-§4.7)。番手 (density) スライダ + 間隔 (pitch) segmented の 2 本を
  // 1 つの fieldset にまとめる (= airbrush の粒の粗さ/噴霧密度と同じ「2 スライダを 1
  // fieldset に収める」構成)。表現パリティ原則によりここに置くことで Classic
  // (PenPopup → PenKindPicker) / Modern (ModernToolProperties) の両方に自動で出る。
  pen === 'tone' ? (
    <fieldset key="tone" className="m-0 flex flex-col gap-2 border-0 p-0" aria-label="トーン">
      <legend className="mb-1 select-none text-[10px] text-[var(--color-text-muted)]">
        トーン
      </legend>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="select-none text-[10px] text-[var(--color-text-muted)]">番手</span>
          <span className="select-none font-mono text-[10px] text-white">
            {Math.round(toneDensity * 100)}%
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={toneDensity}
          onChange={(e) => onToneDensityChange(Number(e.currentTarget.value))}
          aria-label="番手"
          className="w-full accent-[var(--color-accent)]"
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="select-none text-[10px] text-[var(--color-text-muted)]">間隔</span>
          <span className="select-none font-mono text-[10px] text-white">{tonePitch}</span>
        </div>
        <div className="flex gap-1">
          {TONE_PITCH_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              aria-pressed={tonePitch === opt.value}
              aria-label={`間隔 ${opt.label}`}
              onClick={() => onTonePitchChange(opt.value)}
              className={grainChipClass(tonePitch === opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </fieldset>
  ) : null,

  // ドット (pen==='dot' のときだけ、phase11-light-pens-2026-08-21.md §3.4/§3.5/§6)。
  // 間隔 (spacing) 1 本のみ。**単位は掃引ステップであって px ではない**のでラベルは
  // 「間隔」のみで値に単位を付けない (px を名乗らない、§3.4)。
  pen === 'dot' ? (
    <fieldset key="dot" className="m-0 flex flex-col gap-2 border-0 p-0" aria-label="ドット">
      <legend className="mb-1 select-none text-[10px] text-[var(--color-text-muted)]">
        ドット
      </legend>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="select-none text-[10px] text-[var(--color-text-muted)]">間隔</span>
          <span className="select-none font-mono text-[10px] text-white">{dotSpacing}</span>
        </div>
        <input
          type="range"
          min={1}
          max={200}
          step={1}
          value={dotSpacing}
          onChange={(e) => onDotSpacingChange(Number(e.currentTarget.value))}
          aria-label="間隔"
          className="w-full accent-[var(--color-accent)]"
        />
      </div>
    </fieldset>
  ) : null,
];
